import { FilePlus2, Search } from "lucide-react";
import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { JobList } from "../features/svg-jobs/JobList";
import { useSvgJobs } from "../features/svg-jobs/hooks";
import type { JobFilterStatus, JobFilters } from "../lib/types";
import { useJobStore } from "../store/useJobStore";

const validStatuses = new Set<JobFilterStatus>(["all", "active", "completed", "failed", "favorite"]);
const labels: Record<JobFilterStatus, string> = {
  all: "全部任务",
  active: "进行中",
  completed: "已完成",
  failed: "异常任务",
  favorite: "收藏任务",
};

export function JobsPage() {
  const [params, setParams] = useSearchParams();
  const stored = useJobStore((state) => state.filters);
  const setFilters = useJobStore((state) => state.setFilters);
  const statusValue = params.get("status") as JobFilterStatus | null;
  const filters: JobFilters = {
    status: statusValue && validStatuses.has(statusValue) ? statusValue : stored.status,
    search: params.get("q") ?? stored.search,
  };
  const jobsQuery = useSvgJobs(filters);

  useEffect(() => setFilters(filters), [filters.search, filters.status, setFilters]);

  const update = (next: JobFilters) => {
    const query = new URLSearchParams();
    if (next.status !== "all") query.set("status", next.status);
    if (next.search.trim()) query.set("q", next.search.trim());
    setParams(query, { replace: true });
    setFilters(next);
  };

  return (
    <div className="page-container" data-ui-capture={filters.status === "favorite" ? "favorites-list" : "jobs-list"}>
      <header className="page-header">
        <div><span className="kicker">TASK LIBRARY</span><h1>{labels[filters.status]}</h1><p>共 {jobsQuery.data?.length || 0} 条任务，最多显示最近 100 条。</p></div>
        <Link className="primary-button compact" to="/workspace"><FilePlus2 size={16} />创建任务</Link>
      </header>

      <section className="filter-bar">
        <div className="search-box"><Search size={17} /><input aria-label="搜索任务" value={filters.search} placeholder="搜索源文件、引擎或阶段" onChange={(event) => update({ ...filters, search: event.target.value })} /></div>
        <select aria-label="任务状态" value={filters.status} onChange={(event) => update({ ...filters, status: event.target.value as JobFilterStatus })}>
          <option value="all">全部任务</option>
          <option value="active">进行中</option>
          <option value="completed">已完成</option>
          <option value="failed">失败、取消与过期</option>
          <option value="favorite">已收藏</option>
        </select>
      </section>

      {jobsQuery.error && <div className="alert danger">{jobsQuery.error instanceof Error ? jobsQuery.error.message : "任务加载失败"}</div>}
      <JobList jobs={jobsQuery.data || []} loading={jobsQuery.isLoading} grouped emptyMessage="没有符合当前条件的任务" />
    </div>
  );
}
